/**
 * CuidaDF - Tela de Triagem (orientacao por multipla escolha)
 *
 * Implementa as 5 perguntas definidas no documento de revisao clinica.
 * Sem texto livre - apenas opcoes selecionaveis, conforme especificado.
 */

import React, { useState } from "react";
import { enviarTriagem, obterLocalizacaoUsuario } from "../api";
import "../styles/Triagem.css";

const PERGUNTAS = [
  {
    id: "sintoma_principal",
    titulo: "Qual é o seu sintoma principal agora?",
    multiplo: false,
    opcoes: [
      { valor: "febre_calafrios", icone: "🤒", texto: "Febre ou calafrios" },
      { valor: "dificuldade_respirar", icone: "😮‍💨", texto: "Dificuldade para respirar" },
      { valor: "dor_pressao_peito", icone: "💢", texto: "Dor ou pressão no peito" },
      { valor: "nausea_vomito_diarreia", icone: "🤢", texto: "Náusea, vômito ou diarreia" },
      { valor: "dor_cabeca_intensa_subita", icone: "🤕", texto: "Dor de cabeça intensa e súbita" },
      { valor: "dor_ou_lesao", icone: "🩹", texto: "Dor ou lesão (corte, queda, torção)" },
      { valor: "resfriado_tosse_leve", icone: "🤧", texto: "Resfriado, tosse leve ou dor de garganta" },
      { valor: "outro", icone: "❔", texto: "Outro sintoma" },
    ],
  },
  {
    id: "tempo_sintoma",
    titulo: "Há quanto tempo você está sentindo isso?",
    multiplo: false,
    opcoes: [
      { valor: "ultimas_2_horas", icone: "⚡", texto: "Começou agora (últimas 2 horas)" },
      { valor: "menos_24h", icone: "📅", texto: "Hoje (menos de 24 horas)" },
      { valor: "um_a_tres_dias", icone: "🗓️", texto: "Há 1 a 3 dias" },
      { valor: "mais_de_tres_dias", icone: "📆", texto: "Há mais de 3 dias" },
    ],
  },
  {
    id: "sinais_alerta",
    titulo: "Você está sentindo algum destes agora?",
    subtitulo: "Pode marcar mais de uma opção",
    multiplo: true,
    opcoes: [
      { valor: "dificuldade_respirar", icone: "😮‍💨", texto: "Dificuldade para respirar ou falta de ar" },
      { valor: "dor_pressao_peito", icone: "💢", texto: "Dor ou pressão forte no peito" },
      { valor: "desmaio", icone: "😵", texto: "Desmaio ou quase desmaiou" },
      { valor: "sangramento_nao_para", icone: "🩸", texto: "Sangramento que não para" },
      { valor: "confusao_mental", icone: "😶‍🌫️", texto: "Confusão mental ou dificuldade para falar" },
      { valor: "nenhuma", icone: "✅", texto: "Nenhuma dessas" },
    ],
  },
  {
    id: "grupos_risco",
    titulo: "Alguma dessas situações se aplica a você?",
    subtitulo: "Pode marcar mais de uma opção",
    multiplo: true,
    opcoes: [
      { valor: "menos_de_2_anos", icone: "👶", texto: "Tenho menos de 2 anos" },
      { valor: "mais_de_65_anos", icone: "🧓", texto: "Tenho mais de 65 anos" },
      { valor: "gestante", icone: "🤰", texto: "Estou gestante" },
      { valor: "condicao_cronica", icone: "🫀", texto: "Tenho doença cardíaca, diabetes ou pressão alta" },
      { valor: "nenhuma", icone: "✅", texto: "Nenhuma dessas" },
    ],
  },
  {
    id: "intensidade",
    titulo: "Como você descreveria o desconforto agora?",
    multiplo: false,
    opcoes: [
      { valor: "leve", icone: "😊", texto: "Leve — incomoda, mas dá pra seguir o dia" },
      { valor: "moderado", icone: "😐", texto: "Moderado — está difícil de ignorar" },
      { valor: "forte", icone: "😣", texto: "Forte — difícil de fazer outras coisas" },
      { valor: "muito_forte", icone: "😱", texto: "Muito forte — o pior que já senti" },
    ],
  },
];

function Triagem({ onVoltar, onConcluir }) {
  const [perguntaAtual, setPerguntaAtual] = useState(0);
  const [respostas, setRespostas] = useState({});
  const [selecaoAtual, setSelecaoAtual] = useState([]);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState(null);

  const pergunta = PERGUNTAS[perguntaAtual];
  const progresso = ((perguntaAtual + 1) / PERGUNTAS.length) * 100;

  function selecionarOpcao(valor) {
    if (pergunta.multiplo) {
      setSelecaoAtual((atual) => {
        if (atual.includes(valor)) {
          return atual.filter((v) => v !== valor);
        }
        return [...atual, valor];
      });
    } else {
      setSelecaoAtual([valor]);
    }
  }

  async function avancar() {
    const novasRespostas = {
      ...respostas,
      [pergunta.id]: pergunta.multiplo ? selecaoAtual : selecaoAtual[0],
    };
    setRespostas(novasRespostas);
    setSelecaoAtual([]);

    if (perguntaAtual < PERGUNTAS.length - 1) {
      setPerguntaAtual(perguntaAtual + 1);
    } else {
      await concluirTriagem(novasRespostas);
    }
  }

  async function concluirTriagem(respostasFinais) {
    setEnviando(true);
    setErro(null);
    try {
      let localizacao = { latitude: null, longitude: null };
      try {
        localizacao = await obterLocalizacaoUsuario();
      } catch {
        // segue sem localizacao se usuario nao autorizar
      }

      const payload = {
        respostas: {
          sintoma_principal: respostasFinais.sintoma_principal,
          tempo_sintoma: respostasFinais.tempo_sintoma,
          sinais_alerta: respostasFinais.sinais_alerta,
          grupos_risco: respostasFinais.grupos_risco,
          intensidade: respostasFinais.intensidade,
        },
        latitude: localizacao.latitude,
        longitude: localizacao.longitude,
      };

      const resultado = await enviarTriagem(payload);
      onConcluir(resultado);
    } catch (erroEnvio) {
      console.error("Erro ao enviar triagem:", erroEnvio);
      setErro("Não foi possível processar sua avaliação. Tente novamente.");
    } finally {
      setEnviando(false);
    }
  }

  function voltarPergunta() {
    if (perguntaAtual === 0) {
      onVoltar();
    } else {
      setPerguntaAtual(perguntaAtual - 1);
      setSelecaoAtual([]);
    }
  }

  return (
    <div className="tela-triagem">
      <div className="triagem-header">
        <button className="back-btn" onClick={voltarPergunta}>
          ← Voltar
        </button>
        <div className="progress-bar">
          <div className="progress-fill" style={{ width: `${progresso}%` }} />
        </div>
        <p className="progress-text">
          Pergunta {perguntaAtual + 1} de {PERGUNTAS.length}
        </p>
      </div>

      <div className="triagem-content">
        <h2 className="pergunta-texto">{pergunta.titulo}</h2>
        {pergunta.subtitulo && <p className="pergunta-subtitulo">{pergunta.subtitulo}</p>}

        <div className="opcoes">
          {pergunta.opcoes.map((opcao) => (
            <button
              key={opcao.valor}
              className={`opcao ${selecaoAtual.includes(opcao.valor) ? "selected" : ""}`}
              onClick={() => selecionarOpcao(opcao.valor)}
            >
              <span className="opcao-icon">{opcao.icone}</span>
              {opcao.texto}
            </button>
          ))}
        </div>

        {erro && <p className="erro-texto">{erro}</p>}

        <button
          className={`btn-continuar ${selecaoAtual.length > 0 ? "visible" : ""}`}
          onClick={avancar}
          disabled={enviando || selecaoAtual.length === 0}
        >
          {enviando
            ? "Processando..."
            : perguntaAtual < PERGUNTAS.length - 1
            ? "Continuar →"
            : "Ver resultado →"}
        </button>
      </div>
    </div>
  );
}

export default Triagem;
